import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Mail, CheckCircle2, ShieldAlert, Loader2, Send } from 'lucide-react';

interface FerpaConsentReminderCardProps {
  compact?: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  className?: string;
  onSuccess?: () => void;
}

export const FerpaConsentReminderCard: React.FC<FerpaConsentReminderCardProps> = ({
  compact = false,
  title = "Send FERPA Consent Reminder to Parent",
  subtitle = "Enter your parent or legal guardian's email below to send them a secure link to grant FERPA authorization.",
  buttonText = "Send Consent Reminder",
  className = "",
  onSuccess
}) => {
  const { activeUser, resendStudentFerpaConsent } = useWBL();

  // If student already has completed FERPA consent, do not render
  const isCompletedConsent = typeof activeUser?.ferpaConsent === 'object'
    ? activeUser?.ferpaConsent?.granted === true
    : activeUser?.ferpaConsent === true;

  if (isCompletedConsent) {
    return null;
  }

  const [parentEmail, setParentEmail] = useState<string>(
    activeUser?.parentEmail || activeUser?.ferpaConsentRecipientEmail || ''
  );
  const [studentAttested, setStudentAttested] = useState(false);
  const STUDENT_LEGAL_ATTESTATION = "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian.";
  const [isSending, setIsSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    const target = parentEmail.trim().toLowerCase();
    if (!target) {
      setErrorMsg("Please enter a valid parent email address.");
      return;
    }

    if (!studentAttested) {
      setErrorMsg("You must certify the legal attestation checkbox regarding your parent or legal guardian's email address to send this request.");
      return;
    }

    if (!activeUser?.uid) {
      setErrorMsg("Authentication required.");
      return;
    }

    setIsSending(true);
    try {
      const res = await resendStudentFerpaConsent(activeUser.uid, target);
      setSuccessMsg(`Consent authorization email sent to ${res.recipientEmail}!`);
      if (onSuccess) {
        setTimeout(onSuccess, 1800);
      }
    } catch (err: any) {
      console.error("Failed to resend parent FERPA consent:", err);
      setErrorMsg(err.message || "Failed to send consent reminder. Please check the email address and try again.");
    } finally {
      setIsSending(false);
    }
  };

  if (compact) {
    return (
      <div className={`p-4 bg-amber-50/80 border border-amber-200/80 rounded-2xl space-y-3 ${className}`}>
        <div className="flex items-center space-x-2 text-amber-900">
          <Mail className="w-4 h-4 text-amber-600 shrink-0" />
          <h5 className="text-xs font-black uppercase tracking-wider">{title}</h5>
        </div>
        <p className="text-[11px] text-amber-800/90 leading-relaxed font-medium">
          {subtitle}
        </p>

        <form onSubmit={handleSend} className="space-y-2.5">
          <div className="relative">
            <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="parent@example.com"
              required
              className="w-full pl-9 pr-3 py-2 bg-white border border-amber-200 focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs font-bold text-slate-800 outline-none transition-all shadow-2xs"
            />
          </div>

          {/* Legal Attestation Checkbox */}
          <div className="p-2.5 bg-amber-100/70 border border-amber-300/80 rounded-xl">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={studentAttested}
                onChange={(e) => setStudentAttested(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 rounded border-amber-400 text-[#0094c8] focus:ring-[#0094c8] accent-[#0094c8] shrink-0 cursor-pointer"
              />
              <span className="text-[10px] font-semibold text-amber-950 leading-snug select-none">
                "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian."
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSending || !studentAttested}
            className="w-full py-2 px-4 bg-[#0094c8] hover:bg-[#007ca8] text-white rounded-xl text-[11px] font-black uppercase tracking-wider transition-all shadow-xs flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-60"
          >
            {isSending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Dispatching Email...</span>
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" />
                <span>{buttonText}</span>
              </>
            )}
          </button>
        </form>

        {successMsg && (
          <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-[11px] font-bold flex items-start space-x-2 animate-fade-in">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span className="leading-snug">{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-[11px] font-bold flex items-start space-x-2 animate-fade-in">
            <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <span className="leading-snug">{errorMsg}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`p-6 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-4 shadow-2xs ${className}`}>
      <div className="flex items-start space-x-3.5">
        <div className="p-3 bg-white rounded-2xl shadow-2xs border border-slate-100 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5 text-[#0094c8]" />
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-black uppercase text-[#011d3c] tracking-tight">
            {title}
          </h4>
          <p className="text-xs text-slate-500 font-medium leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>

      <form onSubmit={handleSend} className="space-y-3 pt-1">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 relative">
            <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="parent@example.com"
              required
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs font-bold text-slate-800 outline-none transition-all shadow-2xs"
            />
          </div>
          <button
            type="submit"
            disabled={isSending || !studentAttested}
            className="px-5 py-2.5 bg-[#0094c8] hover:bg-[#007ca8] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-xs hover:shadow flex items-center justify-center space-x-2 shrink-0 disabled:opacity-60 cursor-pointer"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>{buttonText}</span>
              </>
            )}
          </button>
        </div>

        {/* Student Legal Attestation Representation Box */}
        <div className="p-3 bg-amber-50/90 border border-amber-300 rounded-xl">
          <label className="flex items-start space-x-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={studentAttested}
              onChange={(e) => setStudentAttested(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-amber-400 text-[#0094c8] focus:ring-[#0094c8] accent-[#0094c8] shrink-0 cursor-pointer"
            />
            <span className="text-[11px] font-semibold text-amber-950 leading-relaxed select-none">
              "I certify under penalty of disciplinary action, loss of program credit, and potential removal from the Work-Based Learning program that the email address provided belongs exclusively to my legal parent or court-appointed legal guardian."
            </span>
          </label>
        </div>
      </form>

      {successMsg && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center space-x-2.5 animate-fade-in shadow-2xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-bold flex items-center space-x-2.5 animate-fade-in shadow-2xs">
          <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}
    </div>
  );
};
